import type { ReactNode } from 'react'

import '@ministries/tailwind-config'
import {
  data,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteLoaderData
} from 'react-router'

import { createServerApiClient, isRealApiClientError, type Locale } from './api'
import I18nProvider from './components/i18n/I18nProvider'
import Desktop from './components/navigation/Desktop'
import Mobile from './components/navigation/Mobile'
import RouteErrorBoundary from './components/shared/RouteErrorBoundary'
import { redirectToSignInWithClearedSession } from './utilities/route.server'
import getSeoMetaTags from './utilities/seo'
import {
  clearSessionCookie,
  createLocaleCookie,
  hasValidSession,
  readSession,
  resolveRequestLocale,
  resolveSubmittedLocale
} from './utilities/session.server'

import type { Route } from './+types/root'

interface DocumentProps {
  children: ReactNode
  className?: string
  locale: Locale
}

interface RootLoaderData {
  currentTenant: {
    id: string
    name: string
    role: string
    slug: string
  } | null
  locale: Locale
}

function isRootLoaderData(value: unknown): value is RootLoaderData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'locale' in value &&
    (value.locale === 'de' || value.locale === 'ru')
  )
}

function Document({ children, className, locale }: DocumentProps) {
  return (
    <html lang={locale} className={className}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
  if (!loaderData) return []

  return getSeoMetaTags({
    instructor: loaderData.instructor,
    title: loaderData.workshopTitle,
    description: loaderData.workshopSubtitle,
    requestInfo: loaderData.requestInfo
  })
}

export function loader({ request }: Route.LoaderArgs) {
  const requestUrl = new URL(request.url)
  const isSignInRoute = requestUrl.pathname === '/sign-in'
  const isTenantSelectRoute = requestUrl.pathname === '/tenant-select'
  const isTenantSwitchRoute =
    isTenantSelectRoute && requestUrl.searchParams.get('switch') === '1'

  const hasSession = hasValidSession(request)
  const session = readSession(request)
  const hasTenantSelection = Boolean(session?.tenantId)

  if (!hasSession && !isSignInRoute) {
    throw redirect('/sign-in')
  }

  if (
    hasSession &&
    !hasTenantSelection &&
    !isSignInRoute &&
    !isTenantSelectRoute
  ) {
    throw redirect('/tenant-select')
  }

  if (
    hasSession &&
    hasTenantSelection &&
    (isSignInRoute || (isTenantSelectRoute && !isTenantSwitchRoute))
  ) {
    throw redirect('/')
  }

  if (hasSession && !hasTenantSelection && isSignInRoute) {
    throw redirect('/tenant-select')
  }

  return data({
    currentTenant:
      session?.tenantId &&
      session.tenantName &&
      session.tenantRole &&
      session.tenantSlug
        ? {
            id: session.tenantId,
            name: session.tenantName,
            role: session.tenantRole,
            slug: session.tenantSlug
          }
        : null,
    locale: resolveRequestLocale(request),
    workshopTitle: 'Minitry',
    workshopSubtitle: 'Ministry management workspace',
    instructor: {
      name: 'Minitry Team'
    },
    requestInfo: {
      domain: requestUrl.origin,
      path: requestUrl.pathname
    }
  })
}

function resolveRedirectTarget(
  value: FormDataEntryValue | null,
  fallbackPathname: string
): string {
  if (typeof value !== 'string' || value.length === 0) {
    return fallbackPathname
  }

  return value.startsWith('/') ? value : fallbackPathname
}

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData()
  const intent =
    typeof formData.get('intent') === 'string'
      ? String(formData.get('intent'))
      : ''

  const session = readSession(request)

  if (intent === 'sign_out') {
    if (session !== null) {
      const api = createServerApiClient(request, session)

      if (api.mode === 'real') {
        try {
          await api.auth.logout(session.refreshToken)
        } catch {
          // Keep sign-out deterministic even when the remote session is already gone.
        }
      }
    }

    return redirectToSignInWithClearedSession()
  }

  if (intent !== 'change_locale') {
    return null
  }

  const locale = resolveSubmittedLocale(formData.get('locale'))
  const requestUrl = new URL(request.url)
  const redirectTo = resolveRedirectTarget(
    formData.get('redirectTo'),
    requestUrl.pathname
  )

  if (locale === null) {
    return redirect(redirectTo)
  }

  if (session !== null) {
    const api = createServerApiClient(request, session)

    try {
      await api.users.updateMe({ locale })
    } catch (caughtError) {
      if (isRealApiClientError(caughtError, 401)) {
        throw redirect('/sign-in', {
          headers: {
            'Set-Cookie': clearSessionCookie()
          }
        })
      }

      throw caughtError
    }
  }

  return redirect(redirectTo, {
    headers: {
      'Set-Cookie': createLocaleCookie(locale)
    }
  })
}

function useRootLocale(): Locale {
  const loaderData: unknown = useRouteLoaderData('root')

  return isRootLoaderData(loaderData) && loaderData.locale === 'ru'
    ? 'ru'
    : 'de'
}

export default function Root() {
  const locale = useRootLocale()
  const location = useLocation()
  const shouldRenderMobileAppNav =
    location.pathname !== '/sign-in' && location.pathname !== '/tenant-select'

  return (
    <Document locale={locale}>
      <I18nProvider locale={locale}>
        <Desktop />
        <Outlet />
        {shouldRenderMobileAppNav ? <Mobile /> : null}
      </I18nProvider>
    </Document>
  )
}

export function ErrorBoundary() {
  const locale = useRootLocale()

  return (
    <Document locale={locale}>
      <I18nProvider locale={locale}>
        <RouteErrorBoundary />
      </I18nProvider>
    </Document>
  )
}
